/**
 * A row of navigation links (cockatrice-ui `src/ui/NavLinks.jsx`, ported to hyperscript; PLAN.md §0). The current link
 * is marked with aria-current="page" and an ink-blue underline. Scrolls sideways on phones rather than wrapping.
 * items: [{ href, label, count?, current? }]  or pass `current` (an href) to mark one.
 * An item with no `href` is a page not open yet: its words, unlinked and muted (PLAN.md §3, step 2). `class` names an
 * item's link for the page that holds it.
 */
import './css/nav-links.css';
import { mergeProps, splitProps } from 'solid-js';
import { each, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

export interface NavLink {
  /** Link target. Without one, the item is shown but not a link (a page that isn't open yet). */
  readonly href?: string | undefined;
  /** Extra class on the item's link. */
  readonly class?: string | undefined;
  /** Visible text. */
  readonly label: Child;
  /** Optional number after the label. */
  readonly count?: number | string | undefined;
  /** Marks this link as the current page. */
  readonly current?: boolean | undefined;
}

export interface NavLinksProps extends Attributes {
  /** The links. */
  readonly items: readonly NavLink[];
  /** Href of the current link (alternative to item.current). */
  readonly current?: string | undefined;
  /** Accessible name when not inside a labelled nav. */
  readonly label?: string | undefined;
  /** Size. Default "md". */
  readonly size?: 'md' | 'sm' | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

export default function NavLinks(props: NavLinksProps): Child {
  const [local, rest] = splitProps(props, ['class', 'items', 'current', 'label', 'size']);
  const isCurrent = (it: NavLink): boolean => it.current ?? (local.current !== undefined && local.current === it.href);
  return el(
    'ul',
    mergeProps(
      {
        class: () => ['ui-nav-links', local.size === 'sm' && 'ui-nav-links--sm', local.class].filter(Boolean).join(' '),
        'aria-label': () => local.label,
      },
      rest,
    ),
    each(
      () => local.items,
      (it) =>
        el(
          'li',
          {},
          it.href === undefined
            ? el('span', { class: ['ui-nav-links__link ui-nav-links__link--off', it.class].filter(Boolean).join(' ') }, () => it.label)
            : el(
                'a',
                { class: ['ui-nav-links__link', it.class].filter(Boolean).join(' '), href: () => it.href, 'aria-current': () => (isCurrent(it) ? 'page' : undefined) },
                () => it.label,
                when(
                  () => it.count !== undefined,
                  () => el('span', { class: 'ui-nav-links__count' }, () => it.count),
                ),
              ),
        ),
    ),
  );
}
